import type {
  AuthPropertiesQuery,
  AuthPropertiesQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import dynamic from 'next/dynamic';
import React, { useContext, useId, useState } from 'react';
import { Modal, useClassNameMapper } from 'react-bootstrap';
import { SharedComponent } from '../../../enums';
import { AuthFlow } from '../../../types/enums';
import type { BannerFormAlertProps } from '../../common/BannerFormAlert/BannerFormAlert';
import BannerFormAlert from '../../common/BannerFormAlert/BannerFormAlert';
import Button from '../../common/Button/Button';
import { ButtonVariant } from '../../common/Button/enums';
import AppContext from '../../context/AppContext/AppContext';
import TenantContext from '../../context/TenantContext';
import useQueryWithTracing from '../../useQueryWithTracing';
import useTranslation from '../../useTranslation';
import authPropertiesQuery from '../AuthProperties.query.graphql';
import ForgotPasswordForm from '../ForgotPasswordForm/ForgotPasswordForm';
import LoginForm from '../LoginForm/LoginForm';
import type { RegistrationFormData } from '../RegistrationForm/RegistrationForm';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import localStyle from './AuthenticationModal.module.pcss';

const MultiAuthLoginForm = dynamic(() => import('../MultiAuthLoginForm/MultiAuthLoginForm'));

interface Props {
  /**
   * Whether the modal is currently being displayed
   */
  show: boolean;
  /**
   * Callback function when the modal is hidden
   *
   * @callback
   * @param boolean - The state of the modal.
   */
  onHide: () => void;
  /**
   * Callback function after login.
   *
   * @callback
   */
  onLogin: () => void;
  /**
   * Callback function after login.
   *
   * @callback
   */
  onRegistration: (formData: RegistrationFormData) => void;
  /**
   * Callback function after form is submitted successfully.
   *
   * @callback
   */
  onForgotPass: () => void;

  /**
   * Type of authentication flow to use.
   */
  type?: AuthFlow;

  /**
   * Props to render Banner form alerts in the form.
   */
  loginBannerAlertProps?: BannerFormAlertProps;
}

/**
 * A modal for the authentication widget components.
 *
 * @author Willi Hyde, Dolan Halbrook
 */
const AuthenticationModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onHide,
  onLogin,
  onRegistration,
  onForgotPass,
  type = AuthFlow.LOGIN,
  loginBannerAlertProps
}) => {
  const {
    formatMessage,
    FormattedMessage,
    loading: textLoading
  } = useTranslation(SharedComponent.AUTHENTICATION_MODAL);
  const { canRegister } = useContext(AppContext);
  const cx = useClassNameMapper(localStyle);
  const tenant = useContext(TenantContext);
  const {
    publicConfig: { multiAuthEnabled }
  } = tenant;
  const uid = useId();

  const { data: authPropertiesData, loading: authPropertiesLoading } = useQueryWithTracing<
    AuthPropertiesQuery,
    AuthPropertiesQueryVariables
  >(module, authPropertiesQuery, {
    skip: !multiAuthEnabled
  });

  const [authFlow, setAuthFlow] = useState(type);

  if (textLoading || authPropertiesLoading) {
    return null;
  }

  function renderNav(authPage: AuthFlow, currentAuthPage: AuthFlow): React.ReactElement {
    return (
      <div className={cx('lia-g-auth-nav-section lia-text-link-wrap')}>
        <FormattedMessage
          id={`nav.text.${authPage}.${currentAuthPage}`}
          values={{
            button: function renderChunks(chunks): React.ReactNode {
              return (
                <Button
                  variant={ButtonVariant.LINK}
                  className={cx('lia-g-link-btn')}
                  onClick={() => setAuthFlow(authPage)}
                >
                  {chunks}
                </Button>
              );
            }
          }}
        />
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExited={() => setAuthFlow(type)}
      size="sm"
      centered
      data-testid="AuthenticationModal"
      aria-labelledby={uid}
    >
      <Modal.Header className={cx('lia-g-subtext-modal-header')} closeButton>
        <Modal.Title as="h2" className={cx('lia-g-subtext-modal-title lia-modal-title')} id={uid}>
          {formatMessage(`title.${authFlow}`)}
        </Modal.Title>
        {authFlow === AuthFlow.FORGOT_PASSWORD && (
          <span className={cx('lia-g-subtext-modal-subtext text-muted')}>
            {formatMessage('description.forgotPassword')}
          </span>
        )}
        {authFlow === AuthFlow.MULTI_AUTH_LOGIN && (
          <span className={cx('lia-g-subtext-modal-subtext')}>
            {authPropertiesData?.community?.authProperties?.multiauthWelcomeMessage}
          </span>
        )}
      </Modal.Header>
      <Modal.Body>
        {authFlow === AuthFlow.REGISTRATION && (
          <>
            <RegistrationForm onRegistration={onRegistration}>
              {multiAuthEnabled
                ? renderNav(AuthFlow.MULTI_AUTH_LOGIN, AuthFlow.REGISTRATION)
                : renderNav(AuthFlow.LOGIN, AuthFlow.REGISTRATION)}
            </RegistrationForm>
          </>
        )}
        {authFlow === AuthFlow.LOGIN && (
          <>
            {loginBannerAlertProps && (
              <BannerFormAlert
                alertMessage={loginBannerAlertProps?.alertMessage}
                alertVariant={loginBannerAlertProps?.alertVariant}
              />
            )}
            <LoginForm onLogin={onLogin}>
              {renderNav(AuthFlow.FORGOT_PASSWORD, AuthFlow.LOGIN)}
              {canRegister && renderNav(AuthFlow.REGISTRATION, AuthFlow.LOGIN)}
            </LoginForm>
            {multiAuthEnabled && renderNav(AuthFlow.MULTI_AUTH_LOGIN, AuthFlow.LOGIN)}
          </>
        )}
        {authFlow === AuthFlow.FORGOT_PASSWORD && (
          <>
            <ForgotPasswordForm onFormSuccess={onForgotPass} />
            {renderNav(AuthFlow.LOGIN, AuthFlow.FORGOT_PASSWORD)}
          </>
        )}
        {authFlow === AuthFlow.MULTI_AUTH_LOGIN && (
          <MultiAuthLoginForm onLogin={onLogin} setAuthFlow={setAuthFlow}>
            {renderNav(AuthFlow.FORGOT_PASSWORD, AuthFlow.LOGIN)}
            {canRegister && renderNav(AuthFlow.REGISTRATION, AuthFlow.LOGIN)}
          </MultiAuthLoginForm>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AuthenticationModal;
